import styled from "styled-components";
import { useState, useContext, useEffect } from "react";
import { Button } from "primereact/button";
import { ReportDatabaseContext } from "../../Contexts/ReportDatabaseContext";
import { CurrentUserContext } from "../../Contexts/CurrentUserContext";
import { OrgTreeContext } from "../../Contexts/OrgTreeContext";
import { useNavigate } from "react-router-dom";
import { findNodeById } from "../../Helpers/OrgTreeFunctions";
import TeamSelector from "./TeamSelector/TeamSelector";
import { report } from "process";

const ReportSummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 10px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  h2 {
    color: var(--accent-color-dark);
    font-size: 25px;
    font-weight: 500;
    width: fit-content;
  }

  @media (max-width: 400px) {
    .p-dropdown {
    }
    h2 {
      font-size: 20px;
    }
  }
`;

const RecentReportsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const WeekRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 10px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  margin-bottom: 10px;

  span {
    display: flex;
    justify-content: left;
    align-items: center;
  }

  h3 {
    text-align: start;
    font-size: 15px;
    font-weight: 400;
    margin: 0;
    padding: 0;
    color: var(--accent-color-dark);
  }

  button {
    min-width: 155px;
  }
`;

const SeeMore = styled.div``;

export default function ReportSummaryPage(props: Props) {
  const reportContext = useContext(ReportDatabaseContext);
  const reportDatabase = reportContext.reportData;
  const orgTree = useContext(OrgTreeContext);
  const user = useContext(CurrentUserContext);
  const [personalReports, setPersonalReports] = useState<ReportData[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>();
  const [weeks, setWeeks] = useState<string[]>([]);
  const [reportBuckets, setReportBuckets] = useState<ReportBucket[]>([]);
  const [numberOfReports, setNumberOfReports] = useState<number>(8);
  const navigate = useNavigate();

  //set personal reports into different buckets for the different teams the user is a part of so that when a team is selected we can change the data set
  useEffect(() => {
    if (reportDatabase && user && user.currentUser && orgTree) {
      let teams: string[] = [];
      const rolesToCheck = [
        "churchStaff",
        "volunteer",
        "pastoralCare",
        "oversight",
      ];

      user.currentUser.PCRoles.forEach((role) => {
        if (
          rolesToCheck.some((r) => role.role.includes(r)) &&
          !teams.includes(role.teamId)
        ) {
          teams.push(role.teamId);
        }
      });

      let reportBuckets = teams.map((teamId) => {
        const teamLabel = findNodeById([orgTree], teamId)?.label;
        return {
          teamId,
          label: teamLabel ? teamLabel : "",
          reports: [] as ReportData[],
        };
      });

      reportDatabase.forEach((report) => {
        if (user.currentUser && report.userId === user.currentUser.userId) {
          const bucket = reportBuckets.find(
            (bucket) => bucket.teamId === report.teamId
          );
          if (bucket) {
            bucket.reports.push(report);
          }
        }
      });

      setReportBuckets(reportBuckets);
      setSelectedTeam(reportBuckets[0].teamId);
      setPersonalReports(reportBuckets[0].reports);
    }
  }, [reportDatabase, user, orgTree]);

  //set personal reports when new team is selected
  useEffect(() => {
    if (selectedTeam && reportBuckets) {
      const bucket = reportBuckets.find(
        (bucket) => bucket.teamId === selectedTeam
      );
      if (bucket) {
        setPersonalReports(bucket.reports);
      }
    }
  }, [selectedTeam, reportBuckets]);

  //set dates for recent reports
  useEffect(() => {
    const getLastThursday = () => {
      const today = new Date();
      const lastMonday = new Date(today);
      lastMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      return lastMonday;
    };

    const generateWeeks = () => {
      const weeks: string[] = [];
      let currentThursday = getLastThursday();

      for (let i = 0; i < numberOfReports; i++) {
        const currentWednesday = new Date(currentThursday);
        currentWednesday.setDate(currentThursday.getDate() + 6);

        const mondayString = currentThursday.toLocaleDateString("en-AU", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const sundayString = currentWednesday.toLocaleDateString("en-AU", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        weeks.push(`${mondayString} to ${sundayString}`);
        currentThursday.setDate(currentThursday.getDate() - 7);
      }

      return weeks;
    };

    setWeeks(generateWeeks());
  }, [numberOfReports]);

  return (
    <ReportSummaryWrapper>
      <Header>
        <h2>My Recent Reports</h2>
        <TeamSelector
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
        />
      </Header>
      <RecentReportsWrapper>
        {weeks.map((week, index) => {
          const [start, end] = week.split(" to ");
          const hasReport = personalReports?.some((report: ReportData) => {
            return (
              new Date(report.reportPeriodDate) >= new Date(start) &&
              new Date(report.reportPeriodDate) <= new Date(end)
            );
          });

          return (
            <WeekRow key={index}>
              <span>
                <h3>
                  {start}
                  <br />
                  {end}
                </h3>
              </span>
              <span>
                <Button
                  label={hasReport ? "Edit Report" : "Create Report"}
                  severity={hasReport ? "success" : "warning"}
                  raised
                  rounded
                  outlined
                  size="small"
                  icon="pi pi-file-edit"
                  iconPos="right"
                  onClick={() =>
                    navigate(
                      `/reports/report-edit/${new Date(
                        start
                      ).toISOString()}to${new Date(
                        end
                      ).toISOString()}in${selectedTeam}`
                    )
                  }
                />
              </span>
            </WeekRow>
          );
        })}
      </RecentReportsWrapper>
      <SeeMore>
        <Button
          label="See More"
          icon="pi pi-chevron-down"
          iconPos="right"
          className="p-button-text"
          onClick={() => setNumberOfReports(numberOfReports + 8)}
        />
      </SeeMore>
    </ReportSummaryWrapper>
  );
}

interface Props {}

interface ReportBucket {
  teamId: string;
  label: string;
  reports: ReportData[];
}
